import { EcsRelativeUrl, RelativeUrl as LambdaRelativeUrl } from "@adora/shared/utils/environment";
import Result from "@adora/shared/types/result";
import fetcher from "@adora/shared/utils/fetch";
import post from "@adora/shared/utils/post";
import pLimit from "p-limit";

import {
  PresignedPostDestinationPath,
  ModelTrainingPresignedPostResponse,
  PresignedPost
} from "@adora/shared/types/presigned-post";

// const ModelTrainingsUrl = RelativeUrl + "/model_trainings/";

export interface UploadImagesProps {
  brandId: number;
  files: File[];
  maxConcurrent?: number;
}

export interface LoraTrainingData {
  steps?: number;
  loraRank?: number;
  batchSize?: number;
  learningRate?: number;
  optimizer?: string;
  resolution?: string;
  captionDropoutRate?: number;
  parentBatchId?: number;
  themeId: number;
  evalLossDatasetPath?: string;
  baseLoraId?: number;
  contentOrStyle?: string;
  wandbSampleInterval?: number;
  wandbSamplePrompt?: string;
  baseModelNameOrPath?: string;
  trainerName: string;
}

export interface TrainLoraProps extends LoraTrainingData {
  brandId: number;
  datasetPath: string;
}
export interface UploadFileProps {
  brandId: number;
  file: File;
}

export interface BulkGenerationProps {
  datasetPath: string;
  brandId: number;
}

function getModelTrainingUrl() {
  return EcsRelativeUrl + "/model_trainings/";
}

async function getUploadUrl(brandId: number) {
  const queryParams = {
    brand_id: brandId.toString()
  };
  return await fetcher<ModelTrainingPresignedPostResponse>(
    LambdaRelativeUrl + "/model_trainings/upload_url",
    queryParams
  );
}

async function uploadFile(presignedPost: PresignedPost, file: Blob): Promise<void> {
  const formData = new FormData();
  Object.keys(presignedPost.fields).forEach((key) => {
    formData.append(key, presignedPost.fields[key]);
  });

  formData.append("file", file);

  const uploadRequestOptions = {
    method: "POST",
    body: formData
  };

  const response = await fetch(presignedPost.url, uploadRequestOptions);
  if (response.status > 204) {
    throw "Invalid response from the server. Expected 200 status code.";
  }
}

async function uploadImages({ brandId, files, maxConcurrent = 50 }: UploadImagesProps): Promise<string> {
  const { presigned_post: presignedPost, dataset_path: datasetPath } = await getUploadUrl(brandId);

  const limit = pLimit(maxConcurrent);
  await Promise.all(files.map((file) => limit(() => uploadFile(presignedPost, file))));

  return datasetPath;
}

async function uploadCsv({ brandId, file }: UploadFileProps): Promise<string> {
  const { presigned_post: presignedPost, dataset_path: datasetPath } = await getUploadUrl(brandId);
  await uploadFile(presignedPost, file);
  return datasetPath + `/${file.name}`;
}

async function trainLora(params: TrainLoraProps): Promise<Result<string>> {
  return await post(
    getModelTrainingUrl() + "launch_lora_training",
    {},
    {
      brand_id: params.brandId,
      theme_id: params.themeId,
      dataset_path: params.datasetPath,
      ...transformTrainLoraToJson(params)
    }
  );
}

async function triggerBulkGeneration({ datasetPath, brandId }: BulkGenerationProps) {
  return await post(
    LambdaRelativeUrl + "/images/generate/bulk",
    {},
    {
      brand_id: brandId,
      dataset_path: datasetPath
    }
  );
}

function transformTrainLoraToJson(params: LoraTrainingData, bulkDatasetPath?: string) {
  return {
    ...(params.parentBatchId && { parent_batch_id: params.parentBatchId }),
    ...(params.steps && { steps: params.steps }),
    ...(params.loraRank && { lora_rank: params.loraRank }),
    ...(params.batchSize && { batch_size: params.batchSize }),
    ...(params.learningRate && { learning_rate: params.learningRate }),
    ...(params.optimizer && { optimizer: params.optimizer }),
    ...(params.resolution && { resolution: params.resolution }),
    ...(params.captionDropoutRate && { caption_dropout_rate: params.captionDropoutRate }),
    ...(params.evalLossDatasetPath && { eval_loss_dataset_path: params.evalLossDatasetPath }),
    ...(params.baseLoraId && { base_lora_id: params.baseLoraId }),
    ...(params.contentOrStyle && { content_or_style: params.contentOrStyle }),
    ...(params.wandbSampleInterval && { wandb_sample_interval: params.wandbSampleInterval }),
    ...(params.wandbSamplePrompt && { wandb_sample_prompt: params.wandbSamplePrompt }),
    ...(params.baseModelNameOrPath && { base_model_name_or_path: params.baseModelNameOrPath }),
    ...(params.trainerName && { replicate_trainer_name: params.trainerName }),
    ...(bulkDatasetPath && { bulk_dataset_path: bulkDatasetPath })
  };
}

export interface LaunchExperimentProps {
  brandId: number;
  name: string;
  description: string;
  referenceImagePaths: string[];
  datasetPath?: string;
  captioningPrompt?: string;
  triggerWord?: string;
  trainLoraParams?: LoraTrainingData;
  themeId?: number;
  bulkDatasetPath?: string;
  productId?: number;
  autoEvalDatapath: string;
}

async function uploadReferenceImage(file: File): Promise<string> {
  const response = await fetcher<PresignedPostDestinationPath>(
    EcsRelativeUrl + "/experiments/reference_image_upload_url"
  );

  await uploadFile(response.presigned_post, file);
  return response.destination_path + "/" + file.name;
}

async function uploadEvalLossDataset(file: File): Promise<string> {
  const response = await fetcher<{ presigned_post: PresignedPost; destination_path: string }>(
    EcsRelativeUrl + "/experiments/eval_loss_dataset_upload_url"
  );

  await uploadFile(response.presigned_post, file);
  return response.destination_path + "/" + file.name;
}

async function launchExperiment(params: LaunchExperimentProps): Promise<Result<string>> {
  let transformedLora = undefined;
  if (params.trainLoraParams) {
    transformedLora = transformTrainLoraToJson(params.trainLoraParams, params.bulkDatasetPath);
  }

  return await post(
    EcsRelativeUrl + "/experiments",
    {},
    {
      brand_id: params.brandId,
      name: params.name,
      description: params.description,
      auto_eval_datapath: params.autoEvalDatapath,
      reference_image_path: params.referenceImagePaths.join(","),
      ...(params.datasetPath && { dataset_path: params.datasetPath }),
      ...(params.captioningPrompt && { captioning_prompt: params.captioningPrompt }),
      ...(params.triggerWord && { trigger_word: params.triggerWord }),
      ...(transformedLora && { train_lora_params: transformedLora }),
      ...(params.themeId && { theme_id: params.themeId }),
      ...(params.productId && { product_id: params.productId }),
      ...(params.autoEvalDatapath && { auto_eval_datapath: params.autoEvalDatapath })
    }
  );
}
const ModelTraining = () => {
  return {
    uploadImages,
    uploadCsv,
    uploadReferenceImage,
    uploadEvalLossDataset,
    trainLora,
    triggerBulkGeneration,
    launchExperiment
  };
};

export default ModelTraining;
